"use client";
import React, { useState, useRef } from "react";
import {
  Box,
  Button,
  FormControlLabel,
  FormLabel,
  InputBase,
  Typography,
} from "@mui/material";
import CustomSwitch from "../../../../components/Switch";
import CustomizedSnackbars from "../../../../components/Toast";
import axiosInstance from "../../../../axios/axiosInstance";

export default function StartTime({
  fleetDetails,
  racePayload,
  onStartTimeUpdate,
}) {
  const [duplicateFlag, setDuplicateFlag] = useState(false);
  const [rows, setRows] = useState([]);
  const [snackbar, setSnackbar] = useState({ text: "", severity: "" });
  const updateBtnRef = useRef(null);

  React.useEffect(() => {
    setRows(fleetDetails || []);
  }, [fleetDetails]);

  const showToast = (text, severity) => {
    setSnackbar({ text, severity });
    setTimeout(() => setSnackbar({ text: "", severity }), 2500);
  };

  const toggleDuplicateFlag = () => setDuplicateFlag((prev) => !prev);

  const isDuplicate = (row) => {
    if (!duplicateFlag) return false;
    return (
      rows.filter(
        (r) => r.hr === row.hr && r.min === row.min && r.sec === row.sec,
      ).length > 1
    );
  };

  const handleCellChange = (id, field, value) => {
    if (!/^\d{0,2}$/.test(value)) return;
    if (field === "hr" && value !== "" && parseInt(value, 10) > 23) return;
    if (
      (field === "min" || field === "sec") &&
      value !== "" &&
      parseInt(value, 10) > 59
    )
      return;
    setRows((prev) =>
      prev.map((r) => (r.id === id ? { ...r, [field]: value } : r)),
    );
  };

  // Holds the committed value when a cell gains focus so we can revert if left empty
  const prevValueRef = useRef({});

  const handleCellFocus = (id, field, value) => {
    prevValueRef.current = { id, field, value };
  };

  const commitCell = (id, field, value) => {
    const prev = prevValueRef.current;
    const revertTo = prev.id === id && prev.field === field ? prev.value : "00";
    // If empty, revert to the value the field had when it was focused
    const final = value === "" ? revertTo : value;
    setRows((r) =>
      r.map((row) =>
        row.id === id
          ? { ...row, [field]: final.length === 1 ? final.padStart(2, "0") : final }
          : row,
      ),
    );
  };

  const handleCellBlur = (id, field, value) => {
    commitCell(id, field, value);
  };

  const handleUpdate = async () => {
    const dbStartTimes = JSON.parse(JSON.stringify(racePayload));
    rows.forEach((row) => {
      const detail = (dbStartTimes.raceSessionsDetailList || []).find(
        (d) => d.raceSessionDetailId === row.id,
      );
      if (detail) {
        detail.startTimeHH = row.hr;
        detail.startTimeMM = row.min;
        detail.startTimeSS = row.sec;
      }
    });
    try {
      await axiosInstance.post("api/clubs/FinishTime/update", dbStartTimes);
      showToast("Start Times Updated Successfully", "success");
      if (onStartTimeUpdate) onStartTimeUpdate([...rows]);
    } catch (error) {
      showToast(error?.response?.data?.message || "Update failed", "error");
    }
  };

  return (
    <Box>
      <CustomizedSnackbars
        severity={snackbar.severity}
        open={snackbar.text !== ""}
        setShowToast={() => setSnackbar({ text: "", severity: "" })}
        text={
          snackbar.text ? (
            <span style={{ whiteSpace: "nowrap" }}>{snackbar.text}</span>
          ) : (
            ""
          )
        }
      />

      {/* Controls row */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2.5,
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
          <FormLabel
            sx={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: 0.6,
              textTransform: "uppercase",
              color: "var(--ty-secondary-color, #42566c)",
              lineHeight: 1,
            }}
          >
            Mark Duplicate Times
          </FormLabel>
          <FormControlLabel
            control={
              <CustomSwitch
                checked={duplicateFlag}
                handleCheckboxChange={toggleDuplicateFlag}
                disabled={false}
                name="Duplicate_Flag"
              />
            }
            label={
              <Typography
                sx={{
                  fontSize: 13,
                  color: "var(--ty-secondary-color, #42566c)",
                }}
              >
                {duplicateFlag ? "Yes" : "No"}
              </Typography>
            }
            labelPlacement="end"
            sx={{ ml: 0, mt: 0.5 }}
          />
        </Box>

        <Button
          ref={updateBtnRef}
          variant="contained"
          onClick={handleUpdate}
          sx={{
            bgcolor: "var(--ty-primary-color, #356dad)",
            color: "#fff",
            borderRadius: "20px",
            textTransform: "none",
            fontWeight: 600,
            fontSize: 12,
            px: 3.5,
            py: 0.5,
            boxShadow: "none",
            "&:hover": {
              bgcolor: "var(--ty-primary-hovercolor, #2b598d)",
              boxShadow: "none",
            },
          }}
        >
          Update
        </Button>
      </Box>

      {/* Grid container */}
      <Box
        sx={{
          bgcolor: "#e8eff5",
          borderRadius: "16px",
          p: 1.5,
        }}
      >
        {/* Column headers */}
        <Box sx={{ display: "flex", alignItems: "center", px: 1.5, mb: 1 }}>
          <Box sx={{ flex: 2.5 }}>
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: 0.6,
                textTransform: "uppercase",
                color: "var(--ty-secondary-color, #42566c)",
              }}
            >
              Fleet
            </Typography>
          </Box>
          {["HR", "MIN", "SEC"].map((col) => (
            <Box key={col} sx={{ flex: 1, textAlign: "center" }}>
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: 0.6,
                  textTransform: "uppercase",
                  color: "var(--ty-secondary-color, #42566c)",
                }}
              >
                {col}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Data rows */}
        {rows.map((row, rowIndex) => (
          <Box
            key={row.id}
            sx={{
              display: "flex",
              alignItems: "center",
              bgcolor: isDuplicate(row) ? "#fff3cd" : "#ffffff",
              borderRadius: "10px",
              px: 1.5,
              py: 0.75,
              mb: 0.75,
              "&:last-child": { mb: 0 },
            }}
          >
            {/* Fleet name */}
            <Box sx={{ flex: 2.5, minWidth: 0 }}>
              <Typography
                title={row.fleet}
                sx={{
                  fontSize: 13,
                  fontWeight: 500,
                  color: "var(--ty-secondary-color, #42566c)",
                  userSelect: "none",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {row.fleet}
              </Typography>
            </Box>

            {/* HR / MIN / SEC editable cells */}
            {["hr", "min", "sec"].map((field) => {
              const isLastCell =
                rowIndex === rows.length - 1 && field === "sec";
              return (
                <Box
                  key={field}
                  sx={{ flex: 1, display: "flex", justifyContent: "center" }}
                >
                  <InputBase
                    value={row[field] ?? ""}
                    onChange={(e) =>
                      handleCellChange(row.id, field, e.target.value)
                    }
                    onFocus={() =>
                      handleCellFocus(row.id, field, row[field] ?? "")
                    }
                    onBlur={(e) =>
                      handleCellBlur(row.id, field, e.target.value)
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        commitCell(row.id, field, e.target.value);
                        e.target.blur();
                      }
                      if (e.key === "Tab") {
                        commitCell(row.id, field, e.target.value);
                        if (isLastCell && !e.shiftKey) {
                          e.preventDefault();
                          updateBtnRef.current?.focus();
                        }
                      }
                    }}
                    inputProps={{
                      maxLength: 2,
                      style: {
                        textAlign: "center",
                        fontSize: 13,
                        fontWeight: 500,
                        color: "var(--ty-secondary-color, #42566c)",
                        padding: "4px 0",
                        width: 44,
                      },
                    }}
                    sx={{
                    bgcolor: "#e8eff5",
                    borderRadius: "8px",
                    px: 0.5,
                    py: 0.25,
                  }}
                />
              </Box>
              );
            })}
          </Box>
        ))}
      </Box>
    </Box>
  );
}
